import {
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useRepository } from '@/ui/state/repositoryContext'
import { useTelemetry } from '@/ui/state/telemetryContext'
import { deriveProjectName } from '@/application/library/deriveProjectName'
import { findProjectByName } from '@/application/library/findProjectByName'
import { UploadDocumentError } from '@/application/upload-document/uploadDocument'
import type { ProjectMetadata } from '@/domain/project/types'
import { UploadConflictDialog } from './UploadConflictDialog'

/**
 * Heavy use cases (markdown pipeline + identity algorithm) lazy-load
 * on the user's first click — keeps the LibraryRoute initial bundle
 * thin. Both modules pull `unified` + `remark-*` + `rehype-*` and the
 * block-identity adapters; ~250KB minified that we don't need until
 * the user actually picks a file.
 */
const loadCreate = () =>
  import('@/application/library/createProjectFromUpload').then((m) => m.createProjectFromUpload)
const loadImport = () =>
  import('@/application/library/importNewVersion').then((m) => m.importNewVersion)

type Props = {
  /** When provided, label override for the button. */
  label?: string
}

type Pending = {
  file: File
  existing: ProjectMetadata
} | null

/**
 * File picker + drag-and-drop entry point (PRD #11, #12).
 *
 * On selection / drop:
 *   1. Derive the would-be project name from the filename.
 *   2. If a project with that name already exists in IDB, open the
 *      `UploadConflictDialog` so the user can choose between
 *      "import as new version of the existing project" (the typical
 *      intent) or "create a brand-new project that happens to share
 *      the name".
 *   3. Otherwise, create the project as before and navigate to it.
 */
export function UploadButton({ label }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const repo = useRepository()
  const telemetry = useTelemetry()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [hover, setHover] = useState(false)
  const [pending, setPending] = useState<Pending>(null)

  function clearInput() {
    if (inputRef.current) inputRef.current.value = ''
  }

  async function handleFile(file: File) {
    setError(null)
    setBusy(true)
    try {
      const candidateName = deriveProjectName(file.name)
      const existing = await findProjectByName(candidateName, repo)
      if (existing) {
        setPending({ file, existing })
        return // dialog drives the next step
      }
      await createAndNavigate(file)
    } catch (cause) {
      reportError(cause)
    } finally {
      setBusy(false)
      // Don't clear input if a dialog is now open — the user may want to retry
      // and the input.value reset is what makes re-picking the same file work.
      if (!pending) clearInput()
    }
  }

  async function createAndNavigate(file: File) {
    const createProjectFromUpload = await loadCreate()
    const { project } = await createProjectFromUpload(file, repo)
    void navigate(`/p/${project.id}`)
  }

  async function importIntoExisting(existing: ProjectMetadata, file: File) {
    const importNewVersion = await loadImport()
    await importNewVersion(existing.id, file, repo, undefined, telemetry)
    void navigate(`/p/${existing.id}`)
  }

  function reportError(cause: unknown) {
    if (cause instanceof UploadDocumentError) {
      setError(t(`upload.errors.${cause.code}`))
    } else {
      setError(cause instanceof Error ? cause.message : String(cause))
    }
  }

  async function onDialogImport() {
    if (!pending) return
    const { file, existing } = pending
    setBusy(true)
    try {
      await importIntoExisting(existing, file)
      setPending(null)
    } catch (cause) {
      reportError(cause)
    } finally {
      setBusy(false)
      clearInput()
    }
  }

  async function onDialogCreate() {
    if (!pending) return
    const { file } = pending
    setBusy(true)
    try {
      await createAndNavigate(file)
      setPending(null)
    } catch (cause) {
      reportError(cause)
    } finally {
      setBusy(false)
      clearInput()
    }
  }

  function onDialogCancel() {
    setPending(null)
    clearInput()
  }

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (file) void handleFile(file)
  }

  function onDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    setHover(false)
    const file = event.dataTransfer.files?.[0]
    if (file) void handleFile(file)
  }

  function onDragOver(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    setHover(true)
  }

  function onDragLeave() {
    setHover(false)
  }

  return (
    <div className="flex flex-col gap-3">
      <label
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className="inline-flex cursor-pointer items-center gap-2 rounded border-2 border-dashed px-4 py-3 text-sm transition"
        style={{
          borderColor: hover ? 'var(--color-accent)' : 'var(--color-border)',
          background: hover ? 'rgba(184,85,42,0.05)' : 'transparent',
        }}
      >
        <span
          className="rounded px-3 py-1"
          style={{
            background: 'var(--color-accent)',
            color: 'var(--color-accent-fg)',
          }}
        >
          {busy ? '…' : (label ?? t('upload.browse'))}
        </span>
        <span style={{ color: 'var(--color-muted)' }}>
          {t('upload.dropOrPick')}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept=".md,.markdown,text/markdown"
          className="sr-only"
          onChange={onChange}
          disabled={busy}
          aria-label={t('upload.label')}
        />
      </label>

      {error && (
        <div
          role="alert"
          className="rounded border p-3 text-sm"
          style={{ borderColor: 'var(--color-down)', color: 'var(--color-down)' }}
        >
          {error}
        </div>
      )}

      {pending && (
        <UploadConflictDialog
          projectName={pending.existing.name}
          busy={busy}
          onImportAsNewVersion={() => void onDialogImport()}
          onCreateNewProject={() => void onDialogCreate()}
          onCancel={onDialogCancel}
        />
      )}
    </div>
  )
}